export interface Survey {
  uuid: string;
  parentUUID?: string;
  organization?: string;
  project: string;
  title: string;
  description: string;
  instructions?: string;
  questions: SurveyQuestion[];
  createDate?: number;
  accessCode?: string;
  submitted?: boolean;
  isValidCode?: boolean;
  expirationTime?: number;
  eventUUID: string;
  eventName: string;
  codeType?: string;
  inCode?: string;
  versions?: Survey[];
  isDisabled?: boolean;
  isLocked?: boolean;
  haveUpdate?: boolean;
  lastSurveyUpdate?: Survey;
  tag?: string;
  image?: string | ArrayBuffer | null;
  imageToDelete?: string[];
  assets?: Record<string, string | any>;
  changedDate?: number;
  settings?: SurveySettings;
  sections?: SurveySection[];
  useSections?: boolean;
  [key: string]: string | any;
}
export interface SurveySettings {
  [key: string]: string | any;
}
export interface SurveySection {
  sectionId: string;
  title: string;
  order: number;
  questionIds: string[]; // Changed from questions: SurveyQuestion[] to questionIds: string[] - questions is now the source of truth
  instructions?: string;
  hideHeader?: boolean; // If true, the section header will be hidden, making it appear as list mode
  // Legacy support: questions property may exist in old data, but questionIds is the new standard
  questions?: SurveyQuestion[];
}
export interface SurveyQuestion {
  order: number;
  name: string;
  questionId: string;
  question: string;
  isRequired: boolean;
  type: SurveyQuestionType;
  options: string[];
  optionKeys?: { [key: string]: string };
  metadata: any;
  isValid: boolean;
  errors?: any[];
  triggers?: any[];
  scores: any[];
  alias?: string;
  table?: any;
}
export interface SurveyQuestionMetadata {
  follow_up_questions: string;
  use_follow_up_question: boolean;
  [key: string]: string | any;
}
export interface Task {
  name: string;
  type: 'data' | 'asset' | 'check-conflicts';
  id: string;
  buffer?: string;
  mimeType?: string;
  /** When set, passed to forms/create-form-assets for standalone tile replication (backend). */
  replicateOnBucket?: string;
  /** Optional key prefix for the replicated object (backend). */
  bucketPrefix?: string;
}
export interface AssetMetadata {
  imageKey: string;
  imageBuffer?: ArrayBuffer;
}

export const SURVEY_FIELD_TYPES = [
  { name: 'Boolean', code: 'boolean' },
  { name: 'Yes / No', code: 'yesno' },
  { name: 'Single Choice', code: 'single-choose' },
  { name: 'Multiple Choice', code: 'multiple-choose' },
  { name: 'Satisfactory Scale', code: 'satisfactory-scale' },
  { name: 'Ranking', code: 'ranking' },
  { name: 'Free Text', code: 'free-text' },
  { name: 'Single Image', code: 'single-image' },
];
export const SURVEY_MULTIPLE_SELECTION_FIELDS = [
  'single-choose',
  'multiple-choose',
  'ranking',
  'single-image',
];

export enum SurveyQuestionType {
  boolean = 'boolean',
  yesno = 'yesno',
  single = 'single-choose',
  multiple = 'multiple-choose',
  scale = 'satisfactory-scale',
  freeText = 'free-text',
  ranking = 'ranking',
  singleImage = 'single-image',
}
export enum SurveyCodeTypes {
  qrcode = 'qr-code',
  pregen = 'pregen-code',
}
export interface SurveyAnswerResult {
  code: string;
  codeType?: string;
  surveyUUID: string;
  projectUUID: string;
  eventUUID: string;
  answers: SurveyAnswer[];
  onlineSubmitted?: boolean;
}
export type PendingSubmissionStatus = 'pending' | 'in_flight' | 'failed';

export interface PendingSurveySubmission {
  id?: number;
  eventUUID: string;
  payload: SurveyAnswerResult;
  createdAt: number;
  retryCount: number;
  status: PendingSubmissionStatus;
  lastError?: string;
  lastAttemptAt?: number;
}

export interface FlushPendingResult {
  synced: number;
  failed: number;
  remaining: number;
}

export type FlushPhase = 'running' | 'paused' | 'complete';

export interface FlushProgress extends FlushPendingResult {
  total: number;
  phase: FlushPhase;
}
export interface SurveyAnswer {
  questionId: string;
  answer: any;
  followUpAnswer?: string;
  isValid?: boolean;
}

export interface CheckCodeResponse {
  code: string;
  codeMessage: string;
  validCode: boolean;
  sendSurveyTimeEnd: number;
}

export enum SurveyErrorTypes {
  INVALID_CODE = 'INVALID-CODE',
  EXPIRED_CODE = 'EXPIRED-CODE',
  NOT_STARTED = 'NOT-STARTED',
}
export const SurveyErrorTypeMessages = {
  [SurveyErrorTypes.INVALID_CODE]: `Oops! It seems the code you entered is not recognized in our system. Please double-check the code and try again.`,
  [SurveyErrorTypes.EXPIRED_CODE]: `It looks like the code you're using has already expired. Codes have a limited validity period, so please ensure to use them before they expire.`,
  [SurveyErrorTypes.NOT_STARTED]: `Just a moment! The survey you're trying to access hasn't started yet. Please check back later when it's scheduled to begin.`,
};
